/**
 * End-to-end encrypted, mutually authenticated peer links.
 *
 * This is the piece that makes "decentralised yet running on Cloudflare"
 * a coherent sentence rather than a contradiction. Relays - Cloudflare
 * Workers, a VPS someone rented, a friend's Raspberry Pi - move ciphertext
 * between peers and can see only that. They cannot read gossip, cannot
 * forge a vote, cannot silently rewrite a vertex, and cannot tell a peer
 * apart from any other. A relay operator who is served an order can drop
 * traffic; that is all. Dropping traffic is survivable, because every node
 * holds several links over several transports and the relay list is itself
 * gossiped (see routing.ts).
 *
 * Handshake: a three-Diffie-Hellman pattern in the shape of Noise XX with
 * known statics. Ephemeral keys give forward secrecy - recording today's
 * ciphertext and stealing the node key tomorrow yields nothing. The
 * ed25519 identity signature over the transcript gives authentication, so
 * a relay in the middle cannot substitute its own keys.
 */

import { gcm } from '@noble/ciphers/aes';
import { x25519 } from '@noble/curves/ed25519';
import { hkdf } from '@noble/hashes/hkdf';
import { sha256 } from '@noble/hashes/sha2';
import { concat, equalBytes, fromHex, randomBytes, toHex, utf8, type Hex } from './bytes.ts';
import { tag } from './hash.ts';
import { linkKeyPair, sign, verify } from './keys.ts';

export interface HandshakeMessage {
  t: 'hs';
  /** ed25519 identity public key. */
  id: Hex;
  /** X25519 long-term link public key, derived from the same seed. */
  link: Hex;
  /** X25519 ephemeral public key, fresh per connection. */
  eph: Hex;
  /** ed25519 signature binding id+link+eph+peer-eph to this session. */
  sig: Hex;
  /** Random session salt from the initiator, echoed by the responder. */
  salt: Hex;
}

export interface SessionKeys {
  send: Uint8Array;
  recv: Uint8Array;
  peerId: Hex;
  handshakeHash: Hex;
}

function transcript(salt: Uint8Array, id: Uint8Array, link: Uint8Array, eph: Uint8Array, peerEph: Uint8Array): Uint8Array {
  return tag('face/noise/transcript/v1', salt, id, link, eph, peerEph);
}

const EMPTY32 = new Uint8Array(32);

export class NoiseInitiator {
  private ephSecret: Uint8Array;
  private ephPublic: Uint8Array;
  private salt: Uint8Array;
  private seed: Uint8Array;
  private idPub: Uint8Array;

  constructor(seed: Uint8Array, idPub: Uint8Array) {
    this.seed = seed;
    this.idPub = idPub;
    this.ephSecret = x25519.utils.randomSecretKey();
    this.ephPublic = x25519.getPublicKey(this.ephSecret);
    this.salt = randomBytes(16);
  }

  message(): HandshakeMessage {
    const link = linkKeyPair(this.seed);
    const t = transcript(this.salt, this.idPub, link.publicKey, this.ephPublic, EMPTY32);
    return {
      t: 'hs',
      id: toHex(this.idPub),
      link: toHex(link.publicKey),
      eph: toHex(this.ephPublic),
      salt: toHex(this.salt),
      sig: toHex(sign(this.seed, t)),
    };
  }

  /** Finish once the responder's message arrives. Throws if it does not check out. */
  finish(peer: HandshakeMessage): SessionKeys {
    const peerId = fromHex(peer.id);
    const peerLink = fromHex(peer.link);
    const peerEph = fromHex(peer.eph);
    if (!equalBytes(fromHex(peer.salt), this.salt)) throw new Error('handshake salt mismatch');
    const peerTranscript = transcript(this.salt, peerId, peerLink, peerEph, this.ephPublic);
    if (!verify(peerId, peerTranscript, peer.sig)) throw new Error('handshake signature invalid');

    const link = linkKeyPair(this.seed);
    const dh1 = x25519.getSharedSecret(this.ephSecret, peerEph);
    const dh2 = x25519.getSharedSecret(this.ephSecret, peerLink);
    const dh3 = x25519.getSharedSecret(link.secretKey, peerEph);
    return derive(concat(dh1, dh2, dh3), this.salt, toHex(this.idPub), peer.id, true);
  }
}

export class NoiseResponder {
  private ephSecret: Uint8Array;
  private ephPublic: Uint8Array;
  private seed: Uint8Array;
  private idPub: Uint8Array;

  constructor(seed: Uint8Array, idPub: Uint8Array) {
    this.seed = seed;
    this.idPub = idPub;
    this.ephSecret = x25519.utils.randomSecretKey();
    this.ephPublic = x25519.getPublicKey(this.ephSecret);
  }

  /** Verify the initiator, produce the reply and the session keys at once. */
  accept(peer: HandshakeMessage): { reply: HandshakeMessage; keys: SessionKeys } {
    const peerId = fromHex(peer.id);
    const peerLink = fromHex(peer.link);
    const peerEph = fromHex(peer.eph);
    const salt = fromHex(peer.salt);
    if (salt.length !== 16) throw new Error('bad handshake salt');
    const peerTranscript = transcript(salt, peerId, peerLink, peerEph, EMPTY32);
    if (!verify(peerId, peerTranscript, peer.sig)) throw new Error('handshake signature invalid');

    const link = linkKeyPair(this.seed);
    const myTranscript = transcript(salt, this.idPub, link.publicKey, this.ephPublic, peerEph);
    const reply: HandshakeMessage = {
      t: 'hs',
      id: toHex(this.idPub),
      link: toHex(link.publicKey),
      eph: toHex(this.ephPublic),
      salt: peer.salt,
      sig: toHex(sign(this.seed, myTranscript)),
    };

    const dh1 = x25519.getSharedSecret(this.ephSecret, peerEph);
    const dh2 = x25519.getSharedSecret(link.secretKey, peerEph);
    const dh3 = x25519.getSharedSecret(this.ephSecret, peerLink);
    const keys = derive(concat(dh1, dh2, dh3), salt, toHex(this.idPub), peer.id, false);
    return { reply, keys };
  }
}

function derive(ikm: Uint8Array, salt: Uint8Array, selfId: Hex, peerId: Hex, initiator: boolean): SessionKeys {
  const okm = hkdf(sha256, ikm, salt, utf8('face/noise/v1'), 64);
  const a = okm.slice(0, 32); // initiator -> responder
  const b = okm.slice(32, 64); // responder -> initiator
  return {
    send: initiator ? a : b,
    recv: initiator ? b : a,
    peerId,
    // Ordered by role, not by "self" and "peer", so both ends of the link
    // compute the same session fingerprint and can compare it out of band.
    handshakeHash: toHex(
      tag('face/noise/session/v1', ikm, utf8(initiator ? selfId : peerId), utf8(initiator ? peerId : selfId)),
    ),
  };
}

/**
 * Framed AES-256-GCM channel. Nonces are a per-direction counter, never
 * reused, and the counter is refused rather than wrapped.
 */
export class SecureChannel {
  private sendCounter = 0n;
  private recvSeen = new Set<string>();
  private recvHighWater = -1n;
  readonly peerId: Hex;
  private sendKey: Uint8Array;
  private recvKey: Uint8Array;

  constructor(keys: SessionKeys) {
    this.sendKey = keys.send;
    this.recvKey = keys.recv;
    this.peerId = keys.peerId;
  }

  private static nonce(counter: bigint): Uint8Array {
    const n = new Uint8Array(12);
    const dv = new DataView(n.buffer);
    dv.setUint32(0, 0x66616365, false); // "face"
    dv.setBigUint64(4, counter, false);
    return n;
  }

  seal(plaintext: Uint8Array): { n: string; c: Hex } {
    if (this.sendCounter >= 0xffffffffffffffffn) throw new Error('channel exhausted - rekey required');
    const counter = this.sendCounter++;
    const ct = gcm(this.sendKey, SecureChannel.nonce(counter)).encrypt(plaintext);
    return { n: counter.toString(10), c: toHex(ct) };
  }

  open(frame: { n: string; c: Hex }): Uint8Array {
    const counter = BigInt(frame.n);
    if (counter < 0n) throw new Error('bad frame counter');
    // Replay window: strictly increasing, with a small reorder tolerance.
    if (counter <= this.recvHighWater - 512n) throw new Error('frame too old - possible replay');
    const key = frame.n;
    if (this.recvSeen.has(key)) throw new Error('duplicate frame - possible replay');
    const pt = gcm(this.recvKey, SecureChannel.nonce(counter)).decrypt(fromHex(frame.c));
    this.recvSeen.add(key);
    if (counter > this.recvHighWater) this.recvHighWater = counter;
    if (this.recvSeen.size > 4096) {
      for (const k of this.recvSeen) {
        if (BigInt(k) <= this.recvHighWater - 512n) this.recvSeen.delete(k);
      }
    }
    return pt;
  }

  sealJson(value: unknown): { n: string; c: Hex } {
    return this.seal(utf8(JSON.stringify(value)));
  }

  openJson<T = unknown>(frame: { n: string; c: Hex }): T {
    return JSON.parse(new TextDecoder().decode(this.open(frame))) as T;
  }
}
